import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, ShieldCheck, Sun, Zap } from "lucide-react";
import { useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { REFERRAL } from "@/constants/testIds";
import { api, formatApiError } from "@/lib/api";
import { isValidPhone } from "@/lib/phone";

const SHARE_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const EMPTY_FORM = {
  first_name: "",
  last_name: "",
  phone: "",
  city: "",
  message: "",
  consent: false,
  website: "",
};

const ReferralLanding = ({ code, session = null }) => {
  const location = useLocation();
  const [referrer, setReferrer] = useState(null);
  const [brand, setBrand] = useState({
    brand_name: "",
    website_url: "https://greentekindia.co.in",
  });
  const [view, setView] = useState("loading");
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  // `s` carries the share id of the dated link. Printed QR codes from before
  // dated shares carry the literal `s=qr` source marker instead, so only a
  // UUID-shaped value is treated as a share id; anything else stays a source
  // hint and the link is handled as an undated legacy link.
  const shareParam = new URLSearchParams(location.search).get("s");
  const shareId = shareParam && SHARE_ID_PATTERN.test(shareParam) ? shareParam : null;
  const source = shareParam === "qr" ? "qr" : "link";

  useEffect(() => {
    let active = true;

    const load = async () => {
      const [brandResult, referrerResult, memberResult] = await Promise.allSettled([
        api.get("/public/brand"),
        shareId
          ? api.get(`/public/referrers/${encodeURIComponent(code)}`, { params: { s: shareId } })
          : api.get(`/public/referrers/${encodeURIComponent(code)}`),
        session
          ? api.get("/members/me").catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
      ]);
      if (!active) return;
      if (brandResult.status === "fulfilled") {
        setBrand(brandResult.value.data);
      }
      try {
        if (referrerResult.status === "rejected") throw referrerResult.reason;
        const referrerResponse = referrerResult.value;
        const memberResponse =
          memberResult.status === "fulfilled" ? memberResult.value : { data: null };

        const memberCode = memberResponse.data?.referral_code;
        setReferrer(referrerResponse.data);
        setView(memberCode?.toLowerCase() === code.toLowerCase() ? "own" : "form");
      } catch (loadError) {
        if (!active) return;
        const status = loadError?.response?.status;
        if (status === 404) setView("invalid");
        else if (status === 410) setView("expired");
        else setView("error");
      }
    };

    setView("loading");
    load();
    return () => {
      active = false;
    };
  }, [code, session, shareId, loadAttempt]);

  const update = (event) => {
    const { name, type, checked, value } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!form.first_name.trim() || !form.last_name.trim() || !form.city.trim()) {
      setError("Enter your name and city to continue.");
      return;
    }
    if (!isValidPhone(form.phone.trim())) {
      setError("Enter a valid mobile number.");
      return;
    }
    if (!form.consent) {
      setError("Consent is required so the team can contact you.");
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await api.post("/public/referrals", {
        code,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
        message: form.message.trim(),
        consent: form.consent,
        source,
        website: form.website,
        ...(shareId ? { share_id: shareId } : {}),
      });
      setView(data.status === "already_referred" ? "dedupe" : "thanks");
    } catch (submitError) {
      const detail = submitError?.response?.data?.detail;
      if (detail === "self_referral") {
        setView("self");
      } else if (detail === "share_expired" || submitError?.response?.status === 410) {
        setView("expired");
      } else {
        setError(formatApiError(submitError, "We could not send your details. Please try again."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (view === "loading") {
    return (
      <main className="public-referral-page" data-testid={REFERRAL.landing}>
        <div className="public-referral-state"><Loader2 className="spin" /><p>Opening your invitation...</p></div>
      </main>
    );
  }

  if (view === "invalid") {
    return (
      <main className="public-referral-page" data-testid={REFERRAL.landing}>
        <section className="public-referral-state public-referral-invalid" data-testid={REFERRAL.invalid}>
          <span className="public-referral-seal"><Sun /></span>
          <h1>This invitation link is not valid</h1>
          <p>Check the link you received or continue from the main website.</p>
          <a className="public-referral-link" href={brand.website_url}>Visit the main website</a>
        </section>
      </main>
    );
  }

  if (view === "expired") {
    return (
      <main className="public-referral-page" data-testid={REFERRAL.landing}>
        <section className="public-referral-state public-referral-expired" data-testid={REFERRAL.expired}>
          <span className="public-referral-seal"><Sun /></span>
          <h1>This referral link has expired</h1>
          <p>Ask your referrer for a fresh one.</p>
          <a className="public-referral-link" href={brand.website_url}>Visit the main website</a>
        </section>
      </main>
    );
  }

  if (view === "error") {
    return (
      <main className="public-referral-page" data-testid={REFERRAL.landing}>
        <section className="public-referral-state" data-testid={REFERRAL.loadError}>
          <span className="public-referral-seal"><Sun /></span>
          <h1>We could not open this invitation</h1>
          <p>Check your connection and try again.</p>
          <Button type="button" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>Try again</Button>
        </section>
      </main>
    );
  }

  const brandName = referrer.brand_name;
  const teamName = brandName.replace(/\s+Alliance$/i, "");

  return (
    <main className="public-referral-page" data-testid={REFERRAL.landing}>
      <div className="public-referral-shell">
        <header className="public-referral-brand">
          <img src="/logo-mark.png" alt="" />
          <strong>{brandName}</strong>
        </header>

        <section className="invitation-pass">
          <div className="invitation-copy">
            <p className="eyebrow">A PERSONAL INVITATION</p>
            <h1>You have been invited by {referrer.first_name}</h1>
            <p>Take the first step toward rooftop solar with a team ready to guide you.</p>
          </div>
          <svg className="invitation-roofline" viewBox="0 0 420 150" aria-hidden="true">
            <path d="M15 126 116 45l58 46 82-70 149 105" />
            <path d="M55 126h310M116 45v81m140-105v105" />
            <circle cx="330" cy="42" r="19" />
          </svg>
          <div className="invitation-benefits" aria-label="Rooftop solar benefits">
            <span><Sun /> Lower monthly energy bills</span>
            <span><Zap /> A system planned for your rooftop</span>
            <span><ShieldCheck /> {brandName} support from survey to switch-on</span>
          </div>
        </section>

        {view === "form" ? (
          <form className="public-referral-form" data-testid={REFERRAL.landingForm} onSubmit={submit} noValidate>
            <div className="public-referral-form-heading">
              <p className="eyebrow">REQUEST A CALL</p>
              <h2>Tell us where to reach you</h2>
              <p>A solar advisor will call within one working day.</p>
            </div>
            <div className="public-referral-fields">
              <label>First name<input required data-testid={REFERRAL.firstNameInput} name="first_name" value={form.first_name} onChange={update} autoComplete="given-name" /></label>
              <label>Last name<input required data-testid={REFERRAL.lastNameInput} name="last_name" value={form.last_name} onChange={update} autoComplete="family-name" /></label>
              <label>Mobile number<input required data-testid={REFERRAL.phoneInput} name="phone" value={form.phone} onChange={update} autoComplete="tel" inputMode="tel" /></label>
              <label>City<input required data-testid={REFERRAL.cityInput} name="city" value={form.city} onChange={update} autoComplete="address-level2" /></label>
              <label className="public-referral-message">Message <span>Optional</span><textarea data-testid={REFERRAL.messageInput} name="message" value={form.message} onChange={update} rows="3" /></label>
              <label className="referral-honeypot" aria-hidden="true">Website<input name="website" value={form.website} onChange={update} tabIndex="-1" autoComplete="off" /></label>
            </div>
            <label className="public-referral-consent">
              <input required data-testid={REFERRAL.consentCheckbox} type="checkbox" name="consent" checked={form.consent} onChange={update} />
              <span>I agree to {brandName} contacting me about rooftop solar and to the <a href="/legal/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.</span>
            </label>
            {error ? <p className="public-referral-error" role="alert">{error}</p> : null}
            <Button className="public-referral-submit" data-testid={REFERRAL.submitButton} type="submit" disabled={submitting}>
              {submitting ? <><Loader2 className="spin" /> Sending...</> : "Request a call"}
            </Button>
          </form>
        ) : null}

        {view === "own" ? (
          <section className="public-referral-result" data-testid={REFERRAL.ownLinkNotice}>
            <CheckCircle2 />
            <h2>This is your personal referral link</h2>
            <p>Share it with someone interested in rooftop solar. Your own details cannot be submitted here.</p>
            <a className="public-referral-link" href="/home">Return to your account</a>
          </section>
        ) : null}

        {view === "thanks" ? (
          <section className="public-referral-result" data-testid={REFERRAL.thankYou}>
            <CheckCircle2 />
            <h2>Thank you. The {teamName} team will call you within one working day.</h2>
            <p><a href={brand.website_url}>Visit the main website</a> or <a href="/login">sign in to your account</a>.</p>
          </section>
        ) : null}

        {view === "dedupe" ? (
          <section className="public-referral-result" data-testid={REFERRAL.dedupeNotice}>
            <CheckCircle2 />
            <h2>You have already been referred</h2>
            <p>Your earlier referral is still active, so there is nothing else you need to send.</p>
          </section>
        ) : null}

        {view === "self" ? (
          <section className="public-referral-result" data-testid={REFERRAL.selfReferralNotice}>
            <ShieldCheck />
            <h2>You cannot refer yourself</h2>
            <p>This link belongs to the account using that phone number. Share it with someone else instead.</p>
          </section>
        ) : null}
      </div>
    </main>
  );
};

export { ReferralLanding };
export default ReferralLanding;
